import { toArabicDigits } from './digits';

/**
 * A mobile number, spelled back to the person reading it.
 *
 * The number is a **credential**, and that is an argument about what is stored
 * and sent — not about what is drawn. Stored and sent it stays ASCII E.164;
 * drawn beside Arabic prose it is a human surface like any other, so it carries
 * Arabic-Indic digits and is isolated in `<bdi>` at the render site. A number
 * beside Arabic prose is exactly the string the bidirectional algorithm
 * reorders.
 *
 * This function is a rendering, never a round trip: nothing it returns is
 * parsed, submitted, or compared.
 */
/**
 * The country codes a stored account identity can carry, and how each one's
 * local form is written.
 *
 * **Yemen is the market.** Every account is `+967…`, and `967` is the only entry
 * a new row can match. `964` is the RETIRED Iraqi market, kept for display only:
 * rows written before the market correction still exist, and an operator has to
 * read them the way their owner wrote them. One rule for both would print an
 * Iraqi number under Yemen's grouping — `٠٧٧٠ ١٢٣ ٤٥٦٧` cut 3-3-3 — which nobody
 * would recognise as their own number.
 *
 * `toLocal` is what the local form puts in front of the subscriber number: Yemen
 * dials its nine digits as they are, the retired market dialled a trunk `0`.
 * `nsn` and `local` are the two ways the same number can arrive — carrying the
 * country code, or already in the local form somebody typed.
 */
const MARKETS = [
  { code: '967', toLocal: (nsn: string) => nsn, nsn: /^7\d{8}$/, local: /^7\d{8}$/, groups: [3, 3, 3] },
  // Retired market, display only — see above.
  {
    code: '964',
    toLocal: (nsn: string) => `0${nsn}`,
    nsn: /^7\d{9}$/,
    local: /^07\d{9}$/,
    groups: [4, 3, 4],
  },
] as const;

/**
 * The local form and the grouping it takes, or `null` for a value this module
 * does not recognise. An unrecognised number is left whole: cutting it into
 * groups it does not have makes it harder to read, not easier.
 */
function toLocalForm(digits: string): { local: string; groups: readonly number[] } | null {
  const withCode = MARKETS.find(
    (market) => digits.startsWith(market.code) && market.nsn.test(digits.slice(market.code.length)),
  );
  if (withCode) {
    return { local: withCode.toLocal(digits.slice(withCode.code.length)), groups: withCode.groups };
  }

  const alreadyLocal = MARKETS.find((market) => market.local.test(digits));
  return alreadyLocal ? { local: digits, groups: alreadyLocal.groups } : null;
}

/**
 * THE GROUP SEPARATOR IS U+00A0, NOT AN ORDINARY SPACE, AND THAT IS A BIDI FIX
 * RATHER THAN A TYPOGRAPHIC ONE.
 *
 * Arabic-Indic digits carry the bidirectional class `AN`. An ordinary space
 * between two of them is a neutral, and UAX #9 rule N1 resolves a neutral
 * flanked by numbers to `R`; the digit groups then sit one embedding level
 * above the separators, and rule L2 reverses the whole run — so `٠٧٧٠ ٠٠٠ ٠١٢٣`
 * DISPLAYS as `٠١٢٣ ٠٠٠ ٠٧٧٠`. Measured on develop: the first group painted at
 * x=1101 and the last at x=1059, i.e. the number was rendered backwards on the
 * public contact page and on every operator surface that prints one.
 *
 * `<bdi>` does not help. It gives `unicode-bidi: isolate` with `dir="auto"`, and
 * `auto` looks for the first STRONG character — `AN` is not strong, so the
 * isolate resolves to `ltr` and the levels inside it come out exactly the same.
 *
 * U+00A0 carries the class `CS`, and rule W4 collapses `AN CS AN` into a single
 * `AN` run before any of that happens: one run, one level, nothing to reverse.
 * It needs no wrapper element and no stylesheet, so it also fixes the numerals
 * written into prose in the copy files, which no render-site attribute could
 * reach. It is additionally the correct character here — a phone number must not
 * break across two lines.
 */
const GROUP_SEPARATOR = '\xa0';

function groupDigits(digits: string, sizes: readonly number[]): string {
  const groups: string[] = [];
  let cursor = 0;

  for (const size of sizes) {
    if (cursor >= digits.length) break;
    groups.push(digits.slice(cursor, cursor + size));
    cursor += size;
  }
  // Anything past the last declared group stays whole rather than being cut.
  if (cursor < digits.length) groups.push(digits.slice(cursor));

  return groups.join(GROUP_SEPARATOR);
}

export function formatPhoneForReading(phone: string): string {
  const digits = phone.replace(/\D/g, '');

  // `+967771234567` and `771234567` are the same subscriber. The local form is
  // what an operator recognises at a glance, so the country code is dropped in
  // favour of the digits a person dials.
  const form = toLocalForm(digits);

  return toArabicDigits(form ? groupDigits(form.local, form.groups) : digits);
}
